import { type ApiContainerAction } from '@Model';
import { type AsyncStateUpdateRequest, type Awaitable, useTranslate } from '@synqo/frontend-component-library';
import { hasRemarks, billsAsAnotherType } from './actionDetails';
import { ContainerBilledAsCell } from './ContainerBilledAsCell';
import { ContainerLineConfirmation } from './ContainerLineConfirmation';
import { ContainerActionLine, ContainerLineFields } from './ContainerLineLayout';
import { ContainerMaterialCell } from './ContainerMaterialCell';
import { canPickNumber, type ContainerOrderState, isContentWeighed, plannedNumberOf } from './containerRowState';
import { ContainerNumberCell } from './ContainerNumberCell';
import { ContainerRemarksCell } from './ContainerRemarksCell';
import { ContainerTypeCell } from './ContainerTypeCell';
import { type ContainerLineField, fieldToOpen, lineState, whyLineCannotBeConfirmed } from './lineConfirmation';
import { type ExchangeContainerEntry } from './plannedContainerEntries';
import { type ContainerMaterial } from './useContainerMaterial';

interface ExchangeContainerRowProps {
    exchange: ExchangeContainerEntry;
    /** Whether the container of the type picked up carries a number. */
    pickupIndividual: boolean;
    /** Whether the container of the type brought carries a number. */
    deliveryIndividual: boolean;
    /** Path to the order; the row's own segment is appended here. */
    baseApiObjectPath: string;
    canEdit: boolean;
    /** Whether the types and the materials may be linked. */
    canSetLinks: boolean;
    canDelete: boolean;
    /** Whether the user may confirm lines and take confirmations back. */
    canMarkForBooking: boolean;
    isMarking: boolean;
    completing: boolean;
    material: ContainerMaterial;
    orderState: ContainerOrderState;
    requestUpdateAction: (updater: AsyncStateUpdateRequest<ApiContainerAction>, actionId: string) => Awaitable<boolean>;
    onDelete: (actionIds: string[]) => void;
    onMarkForBooking: (exchange: ExchangeContainerEntry) => void;
    onUnmarkForBooking: (exchange: ExchangeContainerEntry) => void;
    focused?: boolean;
}

/**
 * An exchange as one line of two rows: the container that goes out and the empty one that comes in.
 * Both halves are confirmed, booked and removed together.
 */
// @ai-generated 2026-10-08T20:07:16+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function ExchangeContainerRow({
    exchange,
    pickupIndividual,
    deliveryIndividual,
    baseApiObjectPath,
    canEdit,
    canSetLinks,
    canDelete,
    canMarkForBooking,
    isMarking,
    completing,
    material,
    orderState,
    requestUpdateAction,
    onDelete,
    onMarkForBooking,
    onUnmarkForBooking,
    focused = false,
}: ExchangeContainerRowProps) {
    const i18n = useTranslate('');
    const { pickup, delivery } = exchange;
    const state = lineState(pickup.booking_status);
    const isClosed = state === 'confirmed' || lineState(delivery.booking_status) === 'confirmed';
    const actionsPath = `${baseApiObjectPath}/container_actions`;
    const blockedBy = whyLineCannotBeConfirmed([pickup, delivery], (action) =>
        action.id === pickup.id ? pickupIndividual : deliveryIndividual,
    );
    const pickupField = focused ? fieldToOpen(pickup, pickupIndividual, completing) : undefined;
    const deliveryField = focused && !pickupField ? fieldToOpen(delivery, deliveryIndividual, completing) : undefined;
    const opens = (action: ApiContainerAction, field: ContainerLineField) =>
        (action.id === pickup.id ? pickupField : deliveryField) === field;

    const typeOf = (action: ApiContainerAction) => (
        <ContainerTypeCell
            key={opens(action, 'type') ? 'open' : 'closed'}
            autoOpen={opens(action, 'type')}
            action={action}
            baseApiObjectPath={actionsPath}
            canEdit={canSetLinks && !isClosed}
            correcting={orderState.correcting}
            requestUpdateState={(updater) => requestUpdateAction(updater, action.id)}
        />
    );

    const materialOf = (action: ApiContainerAction) => (
        <ContainerMaterialCell
            key={opens(action, 'content') ? 'open' : 'closed'}
            autoOpen={opens(action, 'content')}
            actions={[action]}
            baseApiObjectPath={actionsPath}
            canEdit={canSetLinks && !isClosed && !isContentWeighed(action, orderState.positions)}
            correcting={orderState.correcting}
            material={material}
            requestUpdateAction={requestUpdateAction}
        />
    );

    const numberOf = (action: ApiContainerAction, individual: boolean) =>
        individual ? (
            <ContainerNumberCell
                key={opens(action, 'number') ? 'open' : 'closed'}
                autoOpen={opens(action, 'number')}
                completing={completing}
                action={action}
                baseApiObjectPath={actionsPath}
                canEdit={
                    canEdit && !isClosed && canPickNumber(action, orderState.completedAt, orderState.numbersAtEditStart)
                }
                plannedNumber={plannedNumberOf(action, orderState.numbersAtEditStart, orderState.correcting)}
                requestUpdateState={(updater) => requestUpdateAction(updater, action.id)}
            />
        ) : undefined;

    const requestUpdateOf = (action: ApiContainerAction) => (updater: AsyncStateUpdateRequest<ApiContainerAction>) =>
        requestUpdateAction(updater, action.id);

    const details = (
        <>
            <ContainerRemarksCell
                action={pickup}
                label={`${i18n('Bemerkung')} ${i18n('Raus')}`}
                canEdit={canSetLinks && !isClosed}
                requestUpdateState={requestUpdateOf(pickup)}
            />
            <ContainerRemarksCell
                action={delivery}
                label={`${i18n('Bemerkung')} ${i18n('Rein')}`}
                canEdit={canSetLinks && !isClosed}
                requestUpdateState={requestUpdateOf(delivery)}
            />
            <ContainerBilledAsCell
                action={delivery}
                baseApiObjectPath={actionsPath}
                canEdit={canSetLinks && !isClosed}
                requestUpdateState={requestUpdateOf(delivery)}
            />
        </>
    );

    const status = (
        <ContainerLineConfirmation
            action="swap"
            state={state}
            completing={completing}
            canConfirm={canMarkForBooking}
            locked={isMarking}
            blockedBy={blockedBy && i18n(blockedBy)}
            onConfirm={() => onMarkForBooking(exchange)}
            onUnconfirm={() => onUnmarkForBooking(exchange)}
        />
    );

    return (
        <ContainerActionLine
            action="swap"
            removeLabel={i18n('Tausch entfernen')}
            invalid={orderState.foreignToSite.has(pickup.id) ? i18n('Steht nicht am gewählten Standort.') : undefined}
            onRemove={canDelete && !isClosed ? () => onDelete([pickup.id, delivery.id]) : undefined}
            details={details}
            detailsLabel="Bemerkung und Abrechnung"
            hasDetails={hasRemarks(pickup) || hasRemarks(delivery) || billsAsAnotherType(delivery)}
        >
            <ContainerLineFields
                label={i18n('Raus')}
                type={
                    <ContainerTypeCell
                        action={pickup}
                        baseApiObjectPath={actionsPath}
                        canEdit={false}
                        requestUpdateState={(updater) => requestUpdateAction(updater, pickup.id)}
                    />
                }
                material={materialOf(pickup)}
                detail={numberOf(pickup, pickupIndividual)}
                status={status}
            />
            <ContainerLineFields
                label={i18n('Rein: Leer')}
                type={typeOf(delivery)}
                material={materialOf(delivery)}
                detail={numberOf(delivery, deliveryIndividual)}
            />
        </ContainerActionLine>
    );
}
